import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
  createAction,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';
import flatten from 'lodash/flatten';

import { t } from '@vk-hr-tek/core/translations';
import { AppError } from '@vk-hr-tek/core/error';
import {
  showNotification,
  showError,
  showCustomNotification,
} from '@vk-hr-tek/core/notifications';
import { Filter } from '@vk-hr-tek/core/filter';
import {
  GetCompanyUnitTreeDto,
  UnitService,
  UnitType,
} from '@vk-hr-tek/core/units';
import { awaitDispatch } from '@vk-hr-tek/core/redux';

import {
  CompanyEventsStat,
  CompanyGroupUsers,
  CompanyProfiles,
  EditableCompany,
  GetCompanyCeoParams,
  GetCompanyRoleModelParams,
  GetCompanyRoleModelResponse,
  Group,
  SmevValidationErrorsResponse,
  SettingsService,
  GetCompanyCeoResponse,
  UpdateCompanyCeoRequest,
  GetAccessLevelsResponse,
  FilterSelectOption,
  GetCompanyTimezonesParams,
  CompanyTimezonesResponse,
  TimezoneExceptionsResponse,
  GetTimezoneExceptionsParams,
  TimezoneExceptionsRequest,
  EmployeeTimezone,
  GetUnitsTreeResponse,
  CompanyProfilesSettings,
  UnitTypeItem,
  TimezoneEmployeesResponse,
  GetTimezoneEmployeesParams,
  TimezoneEmployeesRequest,
  ToggleCompanySkipAccountSyncRequest,
  ToggleCompanySkipAccountSyncParams,
  GetProfilesQuery,
  UpdateCompanyParams,
  UpdateCompanyRequest,
  UpdateCompanyManagerBlocksParams,
  UpdateCompanyManagerBlocksRequest,
  UpdateCompanySettingsParams,
  UpdateCompanySettingsRequest,
  SmevValidationErrorsRequest,
  SetCompanySmevValidationErrorsParams,
  SmevValidationEsiaRequest,
  SetCompanySmevValidationEsiaParams,
  GetCompanyGroupsParams,
  UpdateCompanyRoleModelParams,
  UpdateCompanyRoleModelRequest,
} from '@app/gen/settings';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import {
  ToolsService,
  UploadFileRequest,
  UploadFileResponse,
} from '@app/gen/tools';
import {
  CreateWidgetBannerParams,
  CreateWidgetBannerQuery,
  DashboardService,
  DeleteWidgetBannerQuery,
  GetAccountWidgetBannerParams,
  GetAccountWidgetBannerQuery,
  GetAccountWidgetBannerResponse,
  UpdateWidgetBannerParams,
  CreateWidgetBannerRequest,
} from '@app/gen/dashboard';
import { Entity } from '@app/types';

import { SettingsCompaniesMapper } from '../../../mappers';
import {
  completeLoading,
  setError,
  SettingsState,
  startLoading,
} from '../../settings.state';
import {
  UpdateCompanyManagerSettingsDto,
  UpdateCompanyTimezoneDto,
  CreateCompanyTimezoneExceptionsDto,
  RemoveEmployeeFromExceptionsDto,
  UpdateEmployeeTimezoneDto,
  UpdateCompanyProfilesSettingsDto,
} from '../../../dto';
import {
  Company,
  SettingsCompanySignTypesRouter,
  SettingsRouter,
} from '../../../types';
import { SettingsWithRootState } from '../..';

const companySettingsNameMapper: Record<
  ToggleCompanySkipAccountSyncRequest['name'],
  keyof Company
> = {
  show_closest_task_widget: 'showClosestTaskWidget',
  separate_units_enabled: 'separateUnit',
  payslips_enabled: 'payslipsEnabled',
  early_employee_event_access_enabled: 'earlyEmployeeEventAccessEnabled',
  manager_starts_from_parent_unit: 'updateManagerStartsParentUnit',
  update_managers_in_events: 'updateManagersInEvents',
  employee_download_pdf_access: 'pdfDownloadAccessEnabled',
  import_legal_units: 'importLegalUnits',
  import_operational_units: 'importOperationalUnits',
  business_trips_enabled: 'businessTripsEnabled',
  search_users_enabled: 'searchUsersEnabled',
};

// Этот маппер выше был добавлен, чтобы срочно зафиксить релизный баг.
// Пришлось так сделать из за того, что в запросе getCompanies в пейлоад попадают данные после маппинга.
// Если бы маппер был в селекторе, этого не пришлось бы делать

export const getCompanies = createAsyncThunk<
  Company[],
  void,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCompanies',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsCompaniesMapper = inject(SettingsCompaniesMapper);
      const settingsService = inject(SettingsService);

      const result = await settingsService.getCompaniesFindByGroupAdmin();

      return settingsCompaniesMapper.processCompanies(result.companies);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompaniesByAccount = createAsyncThunk<
  EditableCompany[],
  { company_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCompaniesByAccount',
  async ({ company_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = await inject(SettingsService);

      const result = await settingsService.getCompaniesFromAccount({
        params: { company_id },
      });

      return result.companies;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getProfiles = createAsyncThunk<
  CompanyProfiles[],
  GetProfilesQuery,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getProfiles',
  async (getProfilesDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = inject(SettingsService);

      const result = await settingsService.getProfiles({
        query: getProfilesDto,
      });

      return result.profiles;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const saveCompanySignSettings = createAsyncThunk<
  void,
  ThunkValuesAndActions<UpdateCompanyParams & UpdateCompanyRequest>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/saveCompanySignSettings',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompany({
        params: { company_id: values.company_id },
        body: {
          sign_type: values.sign_type,
          employees: values.employees,
          employee_sign_type_source: values.employee_sign_type_source,
        },
      });
      actions.resolve(null);

      inject<SettingsCompanySignTypesRouter>(
        SettingsCompanySignTypesRouter,
      ).goToSignTypes();
      dispatch(
        showNotification('Настройки использования ЭЦП успешно изменены!'),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCompanyManagerSettings = createAsyncThunk<
  void,
  ThunkValuesAndActions<UpdateCompanyManagerSettingsDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateCompanyManagerSettings',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id: values.companyId },
        body: {
          import_operational_units: values.importOperationalUnits,
          import_legal_units: values.importLegalUnits,
          show_operational_manager: values.showOperationalManager,
          show_functional_manager: values.showFunctionalManager,
          show_legal_manager: values.showLegalManager,
        },
      });
      actions.resolve(null);
    } catch (err) {
      dispatch(showError('Не удалось изменить настройки!'));
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCompanyManagerBlocks = createAsyncThunk<
  void,
  ThunkValuesAndActions<
    UpdateCompanyManagerBlocksParams & UpdateCompanyManagerBlocksRequest
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateCompanyManagerBlocks',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { updated_count } = await inject(
        SettingsService,
      ).updateCompanyManagerBlocks({
        params: { company_id: values.company_id },
        body: { manager_type: values.manager_type },
      });

      actions.resolve(null);

      dispatch(
        showNotification(
          `Тип оргструктуры изменен! Типы заявок (${updated_count}) шт`,
        ),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updatePayslipEnabled = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updatePayslipEnabled',
  async (
    { company_id, payslips_enabled },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id },
        body: { payslips_enabled },
      });

      dispatch(
        showNotification(
          `${t('settings.actions.companies.employeePayslipsRecievingText')} ${
            payslips_enabled ? 'включено' : 'выключено'
          }`,
        ),
      );
    } catch (err) {
      dispatch(showError('Не удалось изменить настройки!'));

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateBusinessTripsEnabled = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateBusinessTripsEnabled',
  async (
    { company_id, business_trips_enabled },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id },
        body: { business_trips_enabled },
      });

      dispatch(
        showNotification(
          `Раздел «Командировки» ${business_trips_enabled ? 'включен' : 'выключен'}`,
        ),
      );
    } catch (err) {
      dispatch(showError('Не удалось изменить настройки!'));

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateUpdateManagersInEvents = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateUpdateManagersInEvents',
  async (
    { company_id, update_managers_in_events },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id },
        body: { update_managers_in_events },
      });

      dispatch(
        showNotification(
          `Обновление заявок при смене руководителя ${
            update_managers_in_events ? 'включено' : 'выключено'
          }`,
        ),
      );
    } catch (err) {
      dispatch(showError('Не удалось изменить настройки!'));

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateSeparateUnit = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateSeparateUnit',
  async (
    { company_id, separate_units_enabled },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id },
        body: { separate_units_enabled },
      });
      dispatch(
        showNotification(
          `Работа с обособленными подразделениями ${
            separate_units_enabled ? 'включена' : 'выключена'
          }`,
        ),
      );
    } catch (err) {
      dispatch(
        showError(
          `Не удалось ${
            separate_units_enabled ? 'включить' : 'выключить'
          } работу с обособленными подразделениями`,
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateManagerStartsFromParentUnit = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateManagerStartsFromParentUnit',
  async (
    { company_id, manager_starts_from_parent_unit },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id },
        body: {
          manager_starts_from_parent_unit,
        },
      });
      dispatch(
        showNotification(
          `Уровень согласования для руководителя подразделения ${
            manager_starts_from_parent_unit ? 'включено' : 'выключено'
          }`,
        ),
      );
    } catch (err) {
      dispatch(showError('Не удалось изменить настройки!'));
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEarlyEmployeeEventAccess = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEarlyEmployeeEventAccess',
  async (
    { company_id, early_employee_event_access_enabled },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id },
        body: {
          early_employee_event_access_enabled,
        },
      });

      dispatch(
        showNotification(
          `${t('settings.actions.companies.employeeEarlyEventsAccess')} ${
            early_employee_event_access_enabled ? 'включена' : 'выключена'
          }`,
        ),
      );
    } catch (err) {
      dispatch(showError('Не удалось изменить настройки!'));

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateSettingSync = createAsyncThunk<
  Company,
  {
    params: ToggleCompanySkipAccountSyncParams;
    body: ToggleCompanySkipAccountSyncRequest;
    settingName: string;
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateSettingSync',
  async (values, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const settingsService = inject(SettingsService);
      const settingsCompaniesMapper = inject(SettingsCompaniesMapper);

      const newCompanySettings =
        await settingsService.toggleCompanySkipAccountSync(values);

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: !values.body.skip
              ? 'Настройка компании синхронизирована с аккаунтом'
              : 'Синхронизация с аккаунтом приостановлена',
            subtitle: 'Настройка',
            message: values.settingName,
          },
        ]),
      );

      return settingsCompaniesMapper.processCompanies([newCompanySettings])[0];
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: !values.body.skip
              ? 'Не удалось возобновить синхронизацию с аккаунтом'
              : 'Не удалось остановить синхронизацию с аккаунтом',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateDocumentDownloadAccess = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateDocumentDownloadAccess',
  async (
    { company_id, employee_download_pdf_access },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id },
        body: { employee_download_pdf_access },
      });

      dispatch(
        showNotification(
          `${t('settings.actions.companies.documentDownloadAccess')} ${
            !employee_download_pdf_access ? 'включена' : 'выключена'
          }`,
        ),
      );
    } catch (err) {
      dispatch(
        showError(
          `Не удалось ${employee_download_pdf_access ? 'включить' : 'выключить'} ${t(
            'settings.actions.companies.documentDownloadAccess',
          ).toLowerCase()}\nПопробуйте еще раз позже`,
        ),
      );

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateManagerSettingApprove = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateManagerSettingApprove',
  async (
    { company_id, manager_setting_approve },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        body: { manager_setting_approve },
        params: { company_id },
      });
      dispatch(
        showNotification(
          t('settings.company.managerSettingApprove.notification.success'),
        ),
      );
      dispatch(getCompanies());
    } catch (err) {
      dispatch(
        showError(
          t('settings.company.managerSettingApprove.notification.error'),
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const setCompanySmevValidationErrors = createAsyncThunk<
  void,
  ThunkValuesAndActions<
    SmevValidationErrorsRequest & SetCompanySmevValidationErrorsParams
  >,
  ThunkExtra
>(
  'settings/setCompanySmevValidationErrors',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).setCompanySmevValidationErrors({
        params: { company_id: values.company_id },
        body: {
          apply_to_account: values.apply_to_account,
          data: values.data,
        },
      });

      actions.resolve(null);

      dispatch(showNotification('Валидация СМЭВ по типам ошибок изменена!'));
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось изменить валидацию СМЭВ по типам ошибок!',
            message: 'Попробуйте ещё раз позже',
          },
        ]),
      );

      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompanySmevValidationErrors = createAsyncThunk<
  SmevValidationErrorsResponse,
  string,
  ThunkExtra
>(
  'settings/getCompanySmevValidationErrors',
  async (companyId, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsDetailService = inject(SettingsService);

      const result = await settingsDetailService.getCompanySmevValidationErrors(
        {
          params: {
            company_id: companyId,
          },
        },
      );

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompanySmevValidationEsia = createAsyncThunk<
  boolean,
  string,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCompanySmevValidationEsia',
  async (companyId, { rejectWithValue, extra: { inject } }) => {
    try {
      const result = await inject(SettingsService).getCompanySmevValidationEsia(
        {
          params: {
            company_id: companyId,
          },
        },
      );

      return result.ignore_smev_errors_esia;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const setCompanySmevValidationEsia = createAsyncThunk<
  boolean,
  ThunkValuesAndActions<
    SmevValidationEsiaRequest & SetCompanySmevValidationEsiaParams
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/setCompanySmevValidationEsia',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).setCompanySmevValidationEsia({
        params: { company_id: values.company_id },
        body: {
          ignore_smev_errors_esia: values.ignore_smev_errors_esia,
          apply_to_account: values.apply_to_account,
        },
      });

      actions.resolve(null);

      dispatch(showNotification('Валидация СМЭВ по типам ошибок изменена!'));

      return values.ignore_smev_errors_esia;
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось изменить валидацию СМЭВ по типам ошибок!',
            message: 'Попробуйте ещё раз позже',
          },
        ]),
      );

      actions.reject({ [FORM_ERROR]: err });

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompanyCeo = createAsyncThunk<
  GetCompanyCeoResponse,
  GetCompanyCeoParams,
  ThunkExtra
>(
  'settings/getCompanyCeo',
  async (getCompanyCeoParams, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = inject(SettingsService);

      const result = await settingsService.getCompanyCeo({
        params: getCompanyCeoParams,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCompanyCeo = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    params: GetCompanyCeoParams;
    body: UpdateCompanyCeoRequest;
  }>,
  ThunkExtra
>(
  'settings/updateCompanyCeo',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(SettingsService);

      await service.updateCompanyCeo(values);
      actions.resolve(null);
      dispatch(showNotification('Юридическая информация изменена'));
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showError(
          'Не удалось изменить юридическую информацию\nПопробуйте еще раз',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getRoleModelCompanyGroups = createAsyncThunk<
  Group[],
  GetCompanyGroupsParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getRoleModelCompanyGroups',
  async ({ company_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);

      const result = await service.getCompanyGroups({
        params: { company_id },
      });

      return result.groups;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompanyRoleModel = createAsyncThunk<
  GetCompanyRoleModelResponse,
  GetCompanyRoleModelParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCompanyRoleModel',
  async ({ company_id }, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);

      dispatch(
        getRoleModelCompanyGroups({
          company_id,
        }),
      );

      return await service.getCompanyRoleModel({
        params: { company_id },
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCompanyRoleModel = createAsyncThunk<
  void,
  ThunkValuesAndActions<
    UpdateCompanyRoleModelParams & UpdateCompanyRoleModelRequest
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateCompanyRoleModel',
  async (
    { values, actions },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const service = inject(SettingsService);

      const company = getState().settings.companies.find(
        ({ id }) => id === values.company_id,
      );

      await service.updateCompanyRoleModel({
        params: { company_id: values.company_id },
        body: { values: values.values },
      });

      actions.resolve(null);

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: 'Ролевая модель изменена',
            subtitle: 'Компания',
            message: company?.name ?? '',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось изменить ролевую модель',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateRoleModelEnabled = createAsyncThunk<
  void,
  UpdateCompanySettingsRequest & UpdateCompanySettingsParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateRoleModelEnabled',
  async (
    updateRoleModelEnabledDto,
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    try {
      const company = getState().settings.companies.find(
        ({ id }) => id === updateRoleModelEnabledDto.company_id,
      );

      await inject(SettingsService).updateCompanySettings({
        params: { company_id: updateRoleModelEnabledDto.company_id },
        body: {
          role_model_enabled: updateRoleModelEnabledDto.role_model_enabled,
          ...(!updateRoleModelEnabledDto.role_model_enabled &&
            company?.searchUsersEnabled && { search_users_enabled: false }),
        },
      });

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: `Использование ролевой модели ${
              updateRoleModelEnabledDto.role_model_enabled
                ? 'включено'
                : 'выключено'
            }`,
            subtitle: 'Аккаунт',
            message: company?.name ?? '',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось ${
              updateRoleModelEnabledDto.role_model_enabled
                ? 'включить'
                : 'выключить'
            } использование ролевой модели`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateShowClosestTaskWidget = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateShowClosestTaskWidget',
  async (
    { company_id, show_closest_task_widget },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).updateCompanySettings({
        params: { company_id },
        body: {
          show_closest_task_widget,
        },
      });

      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: `Виджет успешно ${
              show_closest_task_widget ? 'включен' : 'отключен'
            }`,
            message: 'Ближайшая задача',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось отключить виджет',
            message: 'Ближайшая задача',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAccessLevel = createAsyncThunk<
  GetAccessLevelsResponse,
  undefined,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAccessLevel',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);

      const result = await service.getAccessLevels();

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const uploadFile = createAsyncThunk<
  UploadFileResponse,
  UploadFileRequest,
  ThunkExtra
>(
  'widgets/uploadFile',
  async (uploadFileRequest, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(ToolsService);

      const result = await service.uploadFile({
        body: uploadFileRequest,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const createWidgetBanner = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    body: CreateWidgetBannerRequest;
    params: CreateWidgetBannerParams;
    query?: CreateWidgetBannerQuery;
  }>,
  ThunkExtra
>(
  'settings/createWidgetBanner',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      await service.createWidgetBanner(values);

      actions.resolve(null);
      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: 'Баннер успешно создан!',
            message: values.body.name,
          },
        ]),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось создать баннер',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateWidgetBanner = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    params: UpdateWidgetBannerParams;
    body: CreateWidgetBannerRequest;
    query?: CreateWidgetBannerQuery;
  }>,
  ThunkExtra
>(
  'settings/updateWidgetBanner',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      await service.updateWidgetBanner({
        body: values.body,
        params: values.params,
        query: values.query,
      });

      actions.resolve(null);
      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: 'Баннер успешно сохранен!',
            message: values.body.name,
          },
        ]),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось сохранить баннер',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getWidgetBanner = createAsyncThunk<
  GetAccountWidgetBannerResponse,
  { params: GetAccountWidgetBannerParams; query?: GetAccountWidgetBannerQuery },
  ThunkExtra<SettingsWithRootState>
>(
  'widgets/getWidgetBanner',
  async (widgetDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getAccountWidgetBanner({
        params: widgetDto.params,
        query: widgetDto?.query,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const deleteWidgetBanner = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    query?: DeleteWidgetBannerQuery;
    params: UpdateWidgetBannerParams;
    body: {
      name: string;
    };
  }>,
  ThunkExtra
>(
  'settings/deleteWidgetBanner',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      await service.deleteWidgetBanner({
        params: values.params,
        query: values.query,
      });

      actions.resolve(null);
      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: 'Баннер успешно удален',
            message: values.body.name,
          },
        ]),
      );
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось удалить баннер',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const resetBanner = createAction<void>('settings/resetBanner');

export const getCompanyUnitTree = createAsyncThunk<
  GetUnitsTreeResponse,
  GetCompanyUnitTreeDto,
  ThunkExtra
>(
  'settings/getCompanyUnitTree',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const unitService = inject(UnitService);

      return await unitService.getUnitTree(dto as GetCompanyUnitTreeDto);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

const getCompanyProfilesSettings = createAsyncThunk<
  CompanyProfilesSettings,
  string,
  ThunkExtra
>(
  'settings/getCompanyProfilesSettings',
  async (companyId, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);

      const companyProfilesSettings = await service.getCompanyProfilesSettings({
        params: { company_id: companyId },
      });

      return companyProfilesSettings;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCompanyProfilesSettings = createAsyncThunk<
  void,
  {
    values: UpdateCompanyProfilesSettingsDto;
    companyId: string;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra
>(
  'settings/updateCompanyProfilesSettings',
  async (
    { values, companyId, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(SettingsService);
      const format = inject(SettingsCompaniesMapper);
      const router = inject<SettingsRouter>(SettingsRouter);

      await service.updateCompanyProfilesSettings({
        params: { company_id: companyId },
        body: format.processUpdateCompanyProfilesSettings(values),
      });

      actions.resolve(null);

      dispatch(showNotification('Профили доступа успешно изменены'));
      router.goToCompanyAccessProfiles(companyId);
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось применить изменения',
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

const getCompanyUnitTypes = createAsyncThunk<
  UnitTypeItem[],
  string,
  ThunkExtra
>(
  'settings/getCompanyUnitTypes',
  async (companyId, { rejectWithValue, extra: { inject } }) => {
    try {
      const format = inject(SettingsCompaniesMapper);
      const unitService = inject(UnitService);

      const unitTypes = await unitService.getUnitTypes({ companyId });

      return format.processUnitTypesSorting(unitTypes.types);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompanyProfilesSetupData = createAsyncThunk<
  CompanyProfilesSettings,
  string,
  ThunkExtra
>(
  'settings/getCompanyProfilesSetupData',
  async (companyId, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);

      const [companyProfilesSettings, unitTypes] = await Promise.all([
        service.getCompanyProfilesSettings({
          params: { company_id: companyId },
        }),
        awaitDispatch(dispatch(getCompanyUnitTypes(companyId))),
      ]);

      const unitType =
        companyProfilesSettings.source === 'units'
          ? companyProfilesSettings.unit_type
          : (unitTypes[0]?.type as UnitType);

      await awaitDispatch(
        dispatch(
          getCompanyUnitTree({
            companyId,
            unitType,
          }),
        ),
      );

      if (companyProfilesSettings?.units?.length) {
        await Promise.all(
          companyProfilesSettings.units.map((unit) =>
            awaitDispatch(
              dispatch(
                getCompanyUnitTree({
                  companyId,
                  unitType,
                  unitId: unit.id,
                }),
              ),
            ),
          ),
        );
      }

      return companyProfilesSettings;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getTimezoneOptions = createAsyncThunk<
  FilterSelectOption[],
  void,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getTimezoneOptions',
  async (_, { rejectWithValue, extra: { inject }, getState }) => {
    const state = getState();
    if (
      state.settings.timezoneOptions &&
      state.settings.timezoneOptions.items.length > 0
    ) {
      return state.settings.timezoneOptions.items;
    }

    try {
      const settingsDetailService = await inject(SettingsService);
      const result = await settingsDetailService.getTimezoneFilterOptions();

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateCompanyTimezone = createAsyncThunk<
  void,
  ThunkValuesAndActions<UpdateCompanyTimezoneDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateCompanyTimezone',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const { companyId, newTimezone } = values;
    try {
      const service = inject(SettingsService);
      await service.updateCompanySettings({
        params: { company_id: companyId },
        body: { timezone: newTimezone.value },
      });
      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: 'Часовой пояс компании успешно изменен',
            message: `${newTimezone.label}`,
          },
        ]),
      );
      dispatch(getCompanies());
      actions.resolve(null);
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось изменить часовой пояс компании',
            message: `Попробуйте еще раз позже`,
          },
        ]),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEmployeesTimezone = createAsyncThunk<
  void,
  ThunkValuesAndActions<CreateCompanyTimezoneExceptionsDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEmployeesTimezone',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const { timezoneExceptions } = values;
    const employeesTimezone: EmployeeTimezone[] = flatten(
      timezoneExceptions.map(({ timezoneId, employees }) =>
        employees.map((employeeId) => ({
          timezone_id: timezoneId,
          employee_id: employeeId,
        })),
      ),
    );
    const exceptionsCount = employeesTimezone.length;

    try {
      const service = inject(SettingsService);
      await service.updateEmployeesTimezone({
        body: { employees: employeesTimezone },
      });
      dispatch(
        showNotification(`Исключения успешно добавлены (${exceptionsCount})`),
      );
      dispatch(getCompanies());
      actions.resolve(null);
    } catch (err) {
      dispatch(
        showError(
          `Не удалось добавить исключения (${exceptionsCount})\nПопробуйте еще раз позже`,
        ),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getCompanyTimezones = createAsyncThunk<
  CompanyTimezonesResponse,
  { params: GetCompanyTimezonesParams },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCompanyTimezones',
  async (values, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = await inject(SettingsService);
      const result = await settingsService.getCompanyTimezones(values);

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

const setTimezoneFilters = createAction<Filter[]>(
  'settings/setTimezoneFilters',
);

export const getTimezoneExceptions = createAsyncThunk<
  TimezoneExceptionsResponse,
  {
    params: GetTimezoneExceptionsParams;
    body?: TimezoneExceptionsRequest;
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getTimezoneExceptions',
  async (dto, { rejectWithValue, getState, extra: { inject }, dispatch }) => {
    try {
      const settingsService = inject(SettingsService);
      const state = getState().settings;

      let filters = state.timezoneExceptions.filters;

      if (!filters) {
        const response =
          await settingsService.getAvailableCompanyTimezoneFilters({
            params: {
              company_id: dto.params.company_id,
            },
          });
        filters = response.filters;

        dispatch(setTimezoneFilters(filters));
      }

      const result = await settingsService.getTimezoneExceptions(dto);

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getTimezoneEmployees = createAsyncThunk<
  TimezoneEmployeesResponse,
  {
    params: GetTimezoneEmployeesParams;
    body?: TimezoneEmployeesRequest;
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getTimezoneEmployees',
  async (dto, { rejectWithValue, getState, extra: { inject }, dispatch }) => {
    try {
      const settingsService = inject(SettingsService);
      const state = getState().settings;

      let filters = state.timezoneExceptions.filters;

      if (!filters) {
        const response =
          await settingsService.getAvailableCompanyTimezoneFilters({
            params: {
              company_id: dto.params.company_id,
            },
          });
        filters = response.filters;

        dispatch(setTimezoneFilters(filters));
      }

      const result = await settingsService.getTimezoneEmployees(dto);

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEmployeesTimezoneExceptions = createAsyncThunk<
  void,
  ThunkValuesAndActions<UpdateEmployeeTimezoneDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEmployeesTimezone',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const { timezoneId, timezoneName, employees, notificationMessages } =
      values;
    const selectedTimezones = employees.map((emp) => ({
      timezone_id: timezoneId,
      employee_id: emp.id,
    }));

    const employee = employees[0];
    const employeesCount = employees.length;
    const isSingle = employeesCount === 1;

    try {
      const service = inject(SettingsService);
      await service.updateEmployeesTimezone({
        body: { employees: selectedTimezones },
      });
      dispatch(
        showCustomNotification([
          {
            type: 'info',
            title: isSingle
              ? notificationMessages.singleSuccess
              : `${notificationMessages.multipleSuccess} (${employeesCount})`,
            message: isSingle
              ? `${employee.fullName}\n${timezoneName}`
              : timezoneName,
          },
        ]),
      );
      actions.resolve(null);
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: isSingle
              ? notificationMessages.singleError
              : `${notificationMessages.multipleError} (${employeesCount})`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );

      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const removeEmployeesFromTimezoneExceptions = createAsyncThunk<
  void,
  ThunkValuesAndActions<RemoveEmployeeFromExceptionsDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/removeEmployeesFromTimezoneExceptions',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const { employees, notificationMessages } = values;

    const selectedTimezones = employees.map((emp) => ({
      employee_id: emp.id,
    }));

    const employee = employees[0];
    try {
      const service = inject(SettingsService);
      await service.updateEmployeesTimezone({
        body: { employees: selectedTimezones },
      });

      const employeesCount = employees.length;
      const isSingle = employeesCount === 1;

      if (isSingle)
        dispatch(
          showCustomNotification([
            {
              type: 'info',
              title: notificationMessages.singleSuccess,
              message: employee.fullName,
            },
          ]),
        );
      else
        dispatch(
          showNotification(
            `${notificationMessages.multipleSuccess} (${employeesCount})`,
          ),
        );

      actions.resolve(null);
    } catch (err) {
      const employeesCount = employees.length;
      const isSingle = employeesCount === 1;

      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: isSingle
              ? notificationMessages.singleError
              : `${notificationMessages.multipleError} (${employeesCount})`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );

      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateTimezoneImportStatus = createAsyncThunk<
  void,
  ThunkValuesAndActions<
    UpdateCompanySettingsParams & UpdateCompanySettingsRequest
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateTimezoneImportStatus',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const settingsService = inject(SettingsService);
    try {
      await settingsService.updateCompanySettings({
        params: { company_id: values.company_id },
        body: { import_timezones: !values.import_timezones },
      });

      dispatch(
        showNotification(
          `Импорт часовых поясов из 1С ${values.import_timezones ? 'выключен' : 'включен'}`,
        ),
      );
      actions.resolve(null);
    } catch (err) {
      dispatch(
        showError(
          `Не удалось ${values.import_timezones ? 'выключить' : 'включить'} импорт часовых поясов из 1С \nПопробуйте еще раз позже`,
        ),
      );
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEmployeeSearchEnabled = createAsyncThunk<
  void,
  UpdateCompanySettingsParams & UpdateCompanySettingsRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEmployeeSearchEnabled',
  async (
    { company_id, search_users_enabled },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(SettingsService);

      await service.updateCompanySettings({
        params: { company_id },
        body: { search_users_enabled },
      });

      dispatch(
        showNotification(
          `${t('settings.account.employeeSearch.label')} ${
            search_users_enabled ? 'включен' : 'выключен'
          }`,
        ),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: `Не удалось ${
              search_users_enabled ? 'включить' : 'выключить'
            } ${t('settings.account.employeeSearch.label').toLowerCase()}`,
            message: 'Попробуйте еще раз позже',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsCompanyReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getCompanies.pending, (state) => {
    startLoading(state);
  });
  builder.addCase(getCompanies.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.companies = payload;
  });
  builder.addCase(getCompanies.rejected, (state, { payload, error }) => {
    setError(state, { payload, error });
  });

  builder.addCase(getCompaniesByAccount.fulfilled, (state, { payload }) => {
    state.companiesByAccount = payload;
  });

  builder.addCase(
    getCompaniesByAccount.rejected,
    (state, { payload, error }) => {
      setError(state, { payload, error });
    },
  );

  builder.addCase(getProfiles.pending, (state) => {
    state.profiles.status = 'loading';
    state.profiles.items = [];
    state.profiles.error = null;
  });
  builder.addCase(getProfiles.fulfilled, (state, { payload }) => {
    state.profiles.status = 'complete';
    state.profiles.items = payload;
    state.profiles.error = null;
  });
  builder.addCase(getProfiles.rejected, (state, { payload, error }) => {
    state.profiles.status = 'failed';
    state.profiles.items = [];
    state.profiles.error =
      payload ||
      ({
        info:
          /* istanbul ignore next */ (error && error.message) ||
          'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(updateCompanyManagerSettings.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.values.companyId) {
        const newCompanySetting = Object.fromEntries(
          Object.entries(meta.arg.values).map(([key, value]) => [key, !!value]),
        );

        return { ...props, ...newCompanySetting };
      }

      return props;
    });
  });

  builder.addCase(updateSeparateUnit.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.company_id) {
        return {
          ...props,
          separateUnit: !!meta.arg.separate_units_enabled,
        };
      }

      return props;
    });
  });

  builder.addCase(updatePayslipEnabled.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.company_id) {
        return {
          ...props,
          payslipsEnabled: !!meta.arg.payslips_enabled,
        };
      }

      return props;
    });
  });
  builder.addCase(updateBusinessTripsEnabled.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.company_id) {
        return {
          ...props,
          businessTripsEnabled: !!meta.arg.business_trips_enabled,
        };
      }

      return props;
    });
  });
  builder.addCase(updateUpdateManagersInEvents.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.company_id) {
        return {
          ...props,
          updateManagersInEvents: !!meta.arg.update_managers_in_events,
        };
      }

      return props;
    });
  });
  builder.addCase(updateSettingSync.fulfilled, (state, { payload, meta }) => {
    state.companies = state.companies.map((company) => {
      if (company.id === meta.arg.params.company_id) {
        const newSettingName = companySettingsNameMapper[meta.arg.body.name];
        const newSkippedSettings = payload.isNotSynced;
        const newSettingValue = payload[newSettingName];

        return {
          ...company,
          isNotSynced: newSkippedSettings,
          [newSettingName]: newSettingValue,
        };
      }

      return company;
    });
  });
  builder.addCase(
    updateManagerStartsFromParentUnit.fulfilled,
    (state, { meta }) => {
      state.companies = state.companies.map((props) => {
        if (props.id === meta.arg.company_id) {
          return {
            ...props,
            updateManagerStartsParentUnit:
              !!meta.arg.manager_starts_from_parent_unit,
          };
        }

        return props;
      });
    },
  );
  builder.addCase(
    updateEarlyEmployeeEventAccess.fulfilled,
    (state, { meta }) => {
      state.companies = state.companies.map((props) => {
        if (props.id === meta.arg.company_id) {
          return {
            ...props,
            earlyEmployeeEventAccessEnabled:
              !!meta.arg.early_employee_event_access_enabled,
          };
        }

        return props;
      });
    },
  );

  builder.addCase(updateDocumentDownloadAccess.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.company_id) {
        return {
          ...props,
          pdfDownloadAccessEnabled: !!meta.arg.employee_download_pdf_access,
        };
      }

      return props;
    });
  });
  builder.addCase(updateShowClosestTaskWidget.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.company_id) {
        return {
          ...props,
          showClosestTaskWidget: meta.arg.show_closest_task_widget,
        };
      }

      return props;
    });
  });

  builder.addCase(getCompanySmevValidationErrors.pending, (state) => {
    state.smevErrors.status = 'loading';
    state.smevErrors.data = [];
    state.smevErrors.error = null;
  });
  builder.addCase(
    getCompanySmevValidationErrors.fulfilled,
    (state, { payload }) => {
      state.smevErrors.status = 'complete';
      state.smevErrors.data = payload.smev_errors;
      state.smevErrors.error = null;
    },
  );
  builder.addCase(
    getCompanySmevValidationErrors.rejected,
    (state, { payload, error }) => {
      state.smevErrors.status = 'failed';
      state.smevErrors.data = [];
      state.smevErrors.error =
        payload ||
        ({
          info:
            /* istanbul ignore next */ (error && error.message) ||
            'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(
    getCompanySmevValidationEsia.fulfilled,
    (state, { payload }) => {
      state.smevErrors.ignoreSmevForEsia = payload;
    },
  );

  builder.addCase(
    setCompanySmevValidationEsia.fulfilled,
    (state, { payload }) => {
      state.smevErrors.ignoreSmevForEsia = payload;
    },
  );

  builder.addCase(getCompanyCeo.pending, (state) => {
    state.companyCeo.status = 'loading';
    state.companyCeo.data = {};
    state.companyCeo.error = null;
  });
  builder.addCase(getCompanyCeo.fulfilled, (state, { payload }) => {
    state.companyCeo.status = 'complete';
    state.companyCeo.data = payload;
    state.companyCeo.error = null;
  });
  builder.addCase(getCompanyCeo.rejected, (state, { payload, error }) => {
    state.companyCeo.status = 'failed';
    state.companyCeo.data = {};
    state.companyCeo.error =
      payload ||
      ({
        info:
          /* istanbul ignore next */ (error && error.message) ||
          'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getRoleModelCompanyGroups.fulfilled, (state, { payload }) => {
    state.companyRoleModel.groups = payload;
  });
  builder.addCase(getCompanyRoleModel.pending, (state) => {
    state.companyRoleModel.status = 'loading';
    state.companyRoleModel.error = null;
  });
  builder.addCase(getCompanyRoleModel.fulfilled, (state, { payload }) => {
    state.companyRoleModel.status = 'complete';
    state.companyRoleModel.roleModelEnabled = payload.role_model_enabled;
    state.companyRoleModel.domains = payload.domains || [];
    state.companyRoleModel.error = null;
  });
  builder.addCase(getCompanyRoleModel.rejected, (state, { payload, error }) => {
    state.companyRoleModel.status = 'failed';
    state.companyRoleModel.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(updateRoleModelEnabled.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.company_id) {
        return {
          ...props,
          roleModelEnabled: !!meta.arg.role_model_enabled,
          ...(!meta.arg.role_model_enabled && {
            searchUsersEnabled: false,
          }),
        };
      }

      return props;
    });
  });

  builder.addCase(getAccessLevel.pending, (state) => {
    state.accessLevels.status = 'loading';
    state.accessLevels.error = null;
  });
  builder.addCase(getAccessLevel.fulfilled, (state, { payload }) => {
    state.accessLevels.status = 'complete';
    state.accessLevels.data = payload.items || [];
  });
  builder.addCase(getAccessLevel.rejected, (state, { payload, error }) => {
    state.accessLevels.status = 'failed';
    state.accessLevels.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getWidgetBanner.pending, (state) => {
    state.banner.status = 'loading';
    state.banner.error = null;
  });
  builder.addCase(getWidgetBanner.fulfilled, (state, { payload }) => {
    state.banner.status = 'complete';
    state.banner.data = payload;
    state.banner.isException = !!payload.company_content_id;
  });
  builder.addCase(getWidgetBanner.rejected, (state, { payload, error }) => {
    state.banner.status = 'failed';
    state.banner.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(resetBanner, (state) => {
    state.banner = {
      status: 'idle',
      data: null,
      error: null,
      isException: false,
    };
  });
  builder.addCase(getCompanyProfilesSettings.pending, (state) => {
    state.companyAccessProfiles.status = 'loading';
    state.companyAccessProfiles.data = null;
    state.companyAccessProfiles.error = null;
  });
  builder.addCase(
    getCompanyProfilesSettings.fulfilled,
    (state, { payload }) => {
      state.companyAccessProfiles.status = 'complete';
      state.companyAccessProfiles.data = payload;
      state.companyAccessProfiles.error = null;
    },
  );
  builder.addCase(
    getCompanyProfilesSettings.rejected,
    (state, { payload, error }) => {
      state.companyAccessProfiles.status = 'failed';
      state.companyAccessProfiles.data = null;
      state.companyAccessProfiles.error =
        payload ||
        ({
          info:
            /* istanbul ignore next */ (error && error.message) ||
            'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
  builder.addCase(getCompanyUnitTypes.pending, (state) => {
    state.companyAccessProfilesSetup.unitTypes.status = 'loading';
  });
  builder.addCase(getCompanyUnitTypes.fulfilled, (state, { payload }) => {
    state.companyAccessProfilesSetup.unitTypes.status = 'complete';
    state.companyAccessProfilesSetup.unitTypes.types = payload;
  });
  builder.addCase(getCompanyUnitTypes.rejected, (state) => {
    state.companyAccessProfilesSetup.unitTypes.status = 'failed';
  });
  builder.addCase(getCompanyUnitTree.pending, (state) => {
    state.companyAccessProfilesSetup.unitsTree.status = 'loading';
  });
  builder.addCase(getCompanyUnitTree.fulfilled, (state, { payload, meta }) => {
    if (meta.arg.unitId) {
      state.companyAccessProfilesSetup.unitsTrees[meta.arg.unitId] = payload;
    } else {
      state.companyAccessProfilesSetup.unitsTree.data = payload;
    }
    state.companyAccessProfilesSetup.unitsTree.status = 'complete';
  });
  builder.addCase(getCompanyUnitTree.rejected, (state) => {
    state.companyAccessProfilesSetup.unitsTree.status = 'failed';
  });
  builder.addCase(getCompanyProfilesSetupData.pending, (state) => {
    state.companyAccessProfilesSetup.status = 'loading';
    state.companyAccessProfilesSetup.data = null;
    state.companyAccessProfilesSetup.error = null;
  });
  builder.addCase(
    getCompanyProfilesSetupData.fulfilled,
    (state, { payload }) => {
      state.companyAccessProfilesSetup.status = 'complete';
      state.companyAccessProfilesSetup.data = payload;
      state.companyAccessProfilesSetup.error = null;
    },
  );
  builder.addCase(
    getCompanyProfilesSetupData.rejected,
    (state, { payload, error }) => {
      state.companyAccessProfilesSetup.status = 'failed';
      state.companyAccessProfilesSetup.data = null;
      state.companyAccessProfilesSetup.error =
        payload ||
        ({
          info:
            /* istanbul ignore next */ (error && error.message) ||
            'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(getTimezoneOptions.pending, (state) => {
    state.timezoneOptions.status = 'loading';
    state.timezoneOptions.error = null;
  });

  builder.addCase(getTimezoneOptions.fulfilled, (state, { payload }) => {
    state.timezoneOptions.status = 'complete';
    state.timezoneOptions.items = payload;
    state.timezoneOptions.error = null;
  });

  builder.addCase(getTimezoneOptions.rejected, (state, { payload, error }) => {
    state.timezoneOptions.status = 'failed';
    state.timezoneOptions.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(getTimezoneExceptions.pending, (state) => {
    state.timezoneExceptions.status = 'loading';
    state.timezoneExceptions.error = null;
  });

  builder.addCase(getTimezoneExceptions.fulfilled, (state, { payload }) => {
    state.timezoneExceptions.status = 'complete';
    state.timezoneExceptions.items = payload.exceptions;
    state.timezoneExceptions.totalCount = payload.total_count;
    state.timezoneExceptions.error = null;
  });

  builder.addCase(
    getTimezoneExceptions.rejected,
    (state, { payload, error }) => {
      state.timezoneExceptions.status = 'failed';
      state.timezoneExceptions.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(updateTimezoneImportStatus.fulfilled, (state, { meta }) => {
    const { values } = meta.arg;

    state.companies = state.companies.map((company) => {
      if (company.id === values.company_id) {
        return {
          ...company,
          importTimezones: !values.import_timezones,
        };
      }
      return company;
    });
  });

  builder.addCase(getTimezoneEmployees.pending, (state) => {
    state.timezoneEmployees.status = 'loading';
    state.timezoneEmployees.error = null;
  });

  builder.addCase(getTimezoneEmployees.fulfilled, (state, { payload }) => {
    state.timezoneEmployees.status = 'complete';
    state.timezoneEmployees.items = payload.employees;
    state.timezoneExceptions.totalCount = payload.total_count;
    state.timezoneEmployees.error = null;
  });

  builder.addCase(
    getTimezoneEmployees.rejected,
    (state, { payload, error }) => {
      state.timezoneEmployees.status = 'failed';
      state.timezoneEmployees.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );

  builder.addCase(getCompanyTimezones.fulfilled, (state, { payload }) => {
    state.companyTimezones.status = 'complete';
    state.companyTimezones.items = payload.timezones;
    state.companyTimezones.error = null;
  });

  builder.addCase(getCompanyTimezones.rejected, (state, { payload, error }) => {
    state.companyTimezones.status = 'failed';
    state.companyTimezones.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(setTimezoneFilters, (state, action) => {
    state.timezoneExceptions.filters = action.payload;
  });

  builder.addCase(updateEmployeeSearchEnabled.fulfilled, (state, { meta }) => {
    state.companies = state.companies.map((props) => {
      if (props.id === meta.arg.company_id) {
        return {
          ...props,
          searchUsersEnabled: !!meta.arg.search_users_enabled,
        };
      }

      return props;
    });
  });
};
